import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
	clearReturnUrl,
	initializeSession,
	selectAuthStatus,
	selectReturnUrl,
	selectUser,
	State,
} from '@lmc-app/store';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { AuthStatus } from '@lmc-app/shared/enum/auth-status.enum';
import { of } from 'rxjs';

/**
 * Unauth Guard (Login Guard)
 *
 * Protects routes that should only be accessible to UNAUTHENTICATED users.
 * Used for login, signup, forgot-password pages.
 *
 * Flow:
 * 1. Check authStatus from NgRx store
 * 2. If UNKNOWN (app just started), dispatch initializeSession() and wait
 * 3. If LOADING (auth check in progress), wait for completion
 * 4. If AUTHENTICATED with valid user, redirect away to returnUrl or dashboard
 * 5. All other statuses (UNAUTHENTICATED, SESSION_EXPIRED, ERROR) → allow access
 *
 * Use Cases:
 * - User manually navigates to /login while already logged in → redirect to dashboard
 * - User refreshes /login page with valid session → redirect to dashboard
 * - User is logged out but has returnUrl in state → after redirect away, they can come back to login
 * - Prevents authenticated users from seeing login form unnecessarily
 *
 * Design Decision:
 * - Session initialization is triggered here if needed (authStatus UNKNOWN)
 * - Loading overlay will show during auth check (via app.component.ts)
 * - This provides smooth UX - no flash of login form before redirect
 * - Fail-safe: If anything goes wrong, allow access to login (secure by default)
 */
export const unauthGuard: CanActivateFn = () => {
	const router = inject(Router);
	const store = inject(Store<State>);

	return store.select(selectAuthStatus).pipe(
		take(1),
		switchMap((authStatus) => {
			// If session not initialized yet (app just started), initialize it
			if (authStatus === AuthStatus.UNKNOWN) {
				store.dispatch(initializeSession());

				// Wait for initialization to complete
				return store.select(selectAuthStatus).pipe(
					filter(
						(status) =>
							status !== AuthStatus.UNKNOWN && status !== AuthStatus.LOADING
					),
					take(1)
				);
			}

			// If currently loading (auth check in progress), wait for it
			if (authStatus === AuthStatus.LOADING) {
				return store.select(selectAuthStatus).pipe(
					filter((status) => status !== AuthStatus.LOADING),
					take(1)
				);
			}

			// Already initialized, return current status immediately
			return of(authStatus);
		}),
		switchMap((authStatus) => {
			// Check if user is authenticated
			return store.select(selectUser).pipe(
				take(1),
				switchMap((user) => {
					// If authenticated with valid user data, redirect away from login
					if (authStatus === AuthStatus.AUTHENTICATED && user) {
						// Get returnUrl to determine where to redirect
						return store.select(selectReturnUrl).pipe(
							take(1),
							map((returnUrl) => {
								// Determine safe redirect destination
								const targetUrl = getSafeRedirectUrl(returnUrl);

								// Clear returnUrl from state
								store.dispatch(clearReturnUrl());

								// Redirect to target
								void router.navigate([targetUrl]);

								// Block access to login page
								return false;
							})
						);
					}

					// All other cases: allow access to login
					// (UNAUTHENTICATED, SESSION_EXPIRED, ERROR, or no user data)
					return of(true);
				})
			);
		})
	);
};

/**
 * Validate and sanitize redirect URL for security
 *
 * Prevents open redirect attacks and redirect loops by ensuring URL is:
 * 1. Internal (starts with /)
 * 2. Not pointing to login page (avoid redirect loop)
 *
 * @param returnUrl - The URL to validate
 * @returns Safe URL to redirect to (returnUrl if valid, /dashboard if invalid/null)
 */
function getSafeRedirectUrl(returnUrl: string | null): string {
	// No returnUrl provided - use default
	if (!returnUrl) {
		return '/dashboard';
	}

	// Ensure it's an internal route (starts with /)
	if (!returnUrl.startsWith('/')) {
		console.warn('Unsafe returnUrl detected (external URL):', returnUrl);
		return '/dashboard';
	}

	// Prevent redirect to login page (would cause loop)
	if (returnUrl.startsWith('/login')) {
		console.warn('Unsafe returnUrl detected (login page):', returnUrl);
		return '/dashboard';
	}

	// URL is safe - use it
	return returnUrl;
}
